import React, { useState, useEffect, useCallback } from 'react';
import SharedHeader from '../../components/shared/SharedHeader';
import ProtectedPage from '../../components/shared/ProtectedPage';
import { usePermissions } from '../../utils/usePermissions';
import { toast } from '../../components/shared/toast';
import { getApiUrl } from '../../utils/backendUrl';
import CaseDetails from './components/CaseDetails';
import NoteCaseForm from './components/NoteCaseForm';
import NotesCasesFilterPopup from './components/NotesCasesFilterPopup';
import NotesCasesSortPopup from './components/NotesCasesSortPopup';
import EmptyState from '../../components/shared/EmptyState';
import Pagination from '../../components/shared/Pagination';
import LoadingOverlay from '../../components/shared/LoadingOverlay';
import ConfirmationModal from '../../components/shared/ConfirmationModal';
import { apiClient } from '../../api/apiClient';
import en from '../../translations/en.json';
import './NotesCases.css';

function NotesCases({ initialCase, onClearInitialCase }) {
  const { canCreate, canEdit, canDelete } = usePermissions();
  const canCreateNotesCases = canCreate('notes_cases');
  const canEditNotesCases = canEdit('notes_cases');
  const canDeleteNotesCases = canDelete('notes_cases');

  const [items, setItems] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  // Search, Sort & Filter state
  const [searchQuery, setSearchQuery] = useState('');
  const [sortOpen, setSortOpen] = useState(false);
  const [selectedSort, setSelectedSort] = useState('Name A-Z');
  const [filterOpen, setFilterOpen] = useState(false);
  const [filters, setFilters] = useState({
    status: null, // 'open' | 'closed' | null
    type: null,   // 'case' | 'note' | null
  });

  // Views & Modals
  const [view, setView] = useState(initialCase ? 'details' : 'dashboard'); // 'dashboard' | 'details' | 'create' | 'edit'
  const [selectedItem, setSelectedItem] = useState(initialCase || null);
  const [itemToEdit, setItemToEdit] = useState(null);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  useEffect(() => {
    const syncFromUrl = async () => {
      const searchParams = new URLSearchParams(window.location.search);
      const paramId = searchParams.get('id');
      if (paramId) {
        if (selectedItem && String(selectedItem.id) === String(paramId)) {
          setView('details');
        } else {
          try {
            setIsLoading(true);
            const data = await apiClient(`/notes-and-cases/${paramId}`);
            if (data && data.id) {
              setSelectedItem(data);
              setView('details');
            }
          } catch (e) {
            console.warn('Failed to load note/case by id:', e);
          } finally {
            setIsLoading(false);
          }
        }
      } else {
        setSelectedItem(null);
        setView('dashboard');
      }
    };

    if (initialCase) {
      setSelectedItem(initialCase);
      setView('details');
      if (onClearInitialCase) onClearInitialCase();
    } else {
      syncFromUrl();
    }

    const handlePopState = () => {
      syncFromUrl();
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [initialCase, onClearInitialCase]);

  const t = (key) => {
    return key.split('.').reduce((obj, i) => (obj ? obj[i] : null), en) || key;
  };

  // Fetch records from backend API
  const fetchNotesCases = useCallback(async () => {
    try {
      setIsLoading(true);
      const params = new URLSearchParams({
        page: String(currentPage),
        limit: '20',
        status: filters.status || 'open'
      });

      if (filters.type) {
        params.append('type', filters.type);
      }

      if (searchQuery.trim()) {
        params.append('search', searchQuery.trim());
      }

      const res = await apiClient(`/notes-and-cases?${params.toString()}`);
      if (res) {
        setItems(res.items || res.records || []);
        setTotalCount(res.totalCount || 0);
        setTotalPages(res.totalPages || 1);
      }
    } catch (err) {
      console.error('Error fetching notes and cases:', err);
      toast.error('Failed to load Notes & Cases data');
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, filters.status, filters.type, searchQuery]);

  useEffect(() => {
    fetchNotesCases();
  }, [currentPage, filters.status, filters.type, searchQuery]);

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({
      ...prev,
      [field]: value,
    }));
    setCurrentPage(1);
  };

  const handleResetFilters = () => {
    setFilters({
      status: null,
      type: null,
    });
    setSearchQuery('');
    setCurrentPage(1);
  };

  // Handle Export Excel
  const handleExport = async () => {
    try {
      setIsExporting(true);
      toast.info('Generating Notes & Cases Excel report...');
      
      const params = new URLSearchParams({
        status: filters.status || 'open',
        export: 'true'
      });
      if (filters.type) {
        params.append('type', filters.type);
      }
      if (searchQuery.trim()) {
        params.append('search', searchQuery.trim());
      }

      const exportUrl = `${getApiUrl('/notes-and-cases/export')}?${params.toString()}`;

      const response = await fetch(exportUrl);
      if (!response.ok) throw new Error('Export generation failed');

      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.setAttribute('download', `Notes_And_Cases_Report.xlsx`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);

      toast.success('Notes & Cases exported successfully!');
    } catch (err) {
      console.error('Export error:', err);
      toast.error('Failed to export Notes & Cases report');
    } finally {
      setIsExporting(false);
    }
  };

  // Create or Update Record
  const handleSaveItem = async (itemPayload) => {
    try {
      setIsLoading(true);
      if (itemToEdit && itemToEdit.id) {
        // Update API
        const res = await apiClient(`/notes-and-cases/${itemToEdit.id}`, {
          method: 'PUT',
          body: itemPayload
        });
        if (res && res.success !== false) {
          toast.success(`${itemPayload.type === 'case' ? 'Case' : 'Note'} updated successfully.`);
          setView('dashboard');
          setItemToEdit(null);
          fetchNotesCases();
        }
      } else {
        // Create API
        const res = await apiClient(`/notes-and-cases`, {
          method: 'POST',
          body: itemPayload
        });
        if (res && res.success !== false) {
          toast.success(`${itemPayload.type === 'case' ? 'Case' : 'Note'} created successfully.`);
          setView('dashboard');
          fetchNotesCases();
        }
      }
    } catch (err) {
      console.error('Error saving note/case:', err);
      toast.error(err.message || 'Failed to save record.');
    } finally {
      setIsLoading(false);
    }
  };

  // Soft Delete Record
  const handleConfirmDelete = async () => {
    if (!itemToDelete) return;
    try {
      setIsLoading(true);
      const res = await apiClient(`/notes-and-cases/${itemToDelete.id}`, {
        method: 'DELETE'
      });
      if (res && res.success !== false) {
        toast.success(`${itemToDelete.type === 'case' ? 'Case' : 'Note'} deleted successfully.`);
        setShowDeleteModal(false);
        setItemToDelete(null);
        fetchNotesCases();
      }
    } catch (err) {
      console.error('Error deleting note/case:', err);
      toast.error(err.message || 'Failed to delete record.');
    } finally {
      setIsLoading(false);
    }
  };

  const isFilterApplied = filters.status !== null || filters.type !== null;

  const sortItems = (list) => {
    if (!list || list.length === 0) return list;
    const sorted = [...list];
    if (selectedSort === 'Name A-Z') {
      return sorted.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    }
    if (selectedSort === 'Name Z-A') {
      return sorted.sort((a, b) => (b.name || '').localeCompare(a.name || ''));
    }
    if (selectedSort === 'Recently Added') {
      return sorted.sort((a, b) => (b.id || 0) - (a.id || 0));
    }
    if (selectedSort === 'Oldest First') {
      return sorted.sort((a, b) => (a.id || 0) - (b.id || 0));
    }
    return sorted;
  };

  const casesList = sortItems(items.filter(item => item.type === 'case'));
  const notesList = sortItems(items.filter(item => item.type === 'note'));

  const handleViewDetails = (item) => {
    setSelectedItem(item);
    setView('details');
    const targetPath = item?.id ? `/notes-cases?id=${item.id}` : '/notes-cases';
    window.history.pushState({ selectedCase: item }, '', targetPath);
  };

  const handleBackFromDetails = () => {
    setSelectedItem(null);
    setView('dashboard');
    window.history.pushState({}, '', '/notes-cases');
  };

  if (view === 'details' && selectedItem) {
    return (
      <CaseDetails 
        item={selectedItem} 
        onBack={handleBackFromDetails} 
        t={t}
      />
    );
  }

  if (view === 'create' || view === 'edit') {
    return (
      <NoteCaseForm 
        initialData={itemToEdit}
        onSave={handleSaveItem} 
        onCancel={() => {
          setItemToEdit(null);
          setView('dashboard');
        }} 
        t={t}
      />
    );
  }

  return (
    <ProtectedPage module="notes_cases">
      <div className="notes-cases-page-wrapper">
        {/* Global Loading Overlay */}
        {(isLoading || isExporting) && <LoadingOverlay isLoading message={isExporting ? 'Generating Excel Export...' : 'Loading Notes & Cases...'} />}

        {/* Header with Search, Sort, Filter, Export, and Add New */}
        <SharedHeader
          title={t('notesCases.title') || 'Note & Cases'}
          searchQuery={searchQuery}
        onSearchChange={(q) => {
          setSearchQuery(q);
          setCurrentPage(1);
        }}
        searchPlaceholder={t('notesCases.searchPlaceholder') || 'Search by name or tags'}
        resultCount={totalCount}
        resultLabel={t('notesCases.resultsCount') || 'Results'}
        showSortFilter={true}
        onSortClick={() => {
          setSortOpen(!sortOpen);
          setFilterOpen(false);
        }}
        onFilterClick={() => {
          setFilterOpen(!filterOpen);
          setSortOpen(false);
        }}
        sortActive={sortOpen}
        filterActive={filterOpen}
        isSortApplied={selectedSort !== 'Name A-Z'}
        isFilterApplied={isFilterApplied}
        onExportClick={handleExport}
        onAddNewClick={canCreateNotesCases ? () => {
          setItemToEdit(null);
          setView('create');
        } : null}
        addNewText={t('common.addNew') || 'Add New'}
        canAddNew={canCreateNotesCases}
      />

      {/* Sort & Filter Popup Dropdowns matching Song tab */}
      <div className="popup-anchor-container">
        {sortOpen && (
          <NotesCasesSortPopup
            selectedSort={selectedSort}
            onSelect={(val) => setSelectedSort(val)}
            onClose={() => setSortOpen(false)}
          />
        )}
        {filterOpen && (
          <NotesCasesFilterPopup
            filters={filters}
            onFilterChange={handleFilterChange}
            onReset={handleResetFilters}
            onClose={() => setFilterOpen(false)}
          />
        )}
      </div>

      {/* Cases Section */}
      {(!filters.type || filters.type === 'case') && (
        <div className="notes-cases-section">
          <h2 className="notes-cases-section-title">
            {t('notesCases.casesHeading') || 'Cases'}
          </h2>
          <div className="notes-cases-cards-container">
            {casesList.map(item => (
              <div key={item.id} className="note-case-card">
                <div>
                  <div className="card-top-header">
                    <h4 className="card-item-name">{item.name}</h4>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      {(item.status === 0 || item.status === '0') && (
                        <span style={{ fontSize: '10px', fontWeight: '700', textTransform: 'uppercase', backgroundColor: '#fee2e2', color: '#ef4444', border: '1px solid #fca5a5', padding: '2px 8px', borderRadius: '10px' }}>
                          Closed
                        </span>
                      )}
                      <span className={`priority-badge ${item.priority || 'medium'}`}>
                        {item.priority || 'medium'}
                      </span>
                    </div>
                  </div>
                  <div className="card-item-tags">{item.tags || '—'}</div>
                  <div className="card-item-situation">
                    Current Situation: {item.currentSituation || 'start'}
                  </div>
                </div>

                <div className="card-footer-row">
                  <button 
                    type="button" 
                    className="card-view-more-btn"
                    onClick={() => handleViewDetails(item)}
                  >
                    {t('notesCases.viewMore') || 'View More >'}
                  </button>

                  <div className="card-action-icons">
                    {canEditNotesCases && (
                      <button
                        type="button"
                        className="card-icon-btn"
                        disabled={item.status === 0 || item.status === '0'}
                        onClick={() => {
                          if (item.status === 0 || item.status === '0') {
                            toast.warning('Closed cases cannot be edited.');
                            return;
                          }
                          setItemToEdit(item);
                          setView('edit');
                        }}
                        style={(item.status === 0 || item.status === '0') ? { opacity: 0.35, cursor: 'not-allowed' } : {}}
                        title={(item.status === 0 || item.status === '0') ? "Closed cases cannot be edited" : "Edit Case"}
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                          <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                        </svg>
                      </button>
                    )}
                    {canDeleteNotesCases && (
                      <button
                        type="button"
                        className="card-icon-btn delete"
                        onClick={() => {
                          setItemToDelete(item);
                          setShowDeleteModal(true);
                        }}
                        title="Delete Case"
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <polyline points="3 6 5 6 21 6" />
                          <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                        </svg>
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
            {casesList.length === 0 && (
              <EmptyState title="No Cases Found" subtitle="No active cases match your search criteria." hasSearch={true} />
            )}
          </div>
        </div>
      )}

      {/* Notes Section */}
      {(!filters.type || filters.type === 'note') && (
        <div className="notes-cases-section" style={{ marginTop: '24px' }}>
          <h2 className="notes-cases-section-title">
            {t('notesCases.notesHeading') || 'Notes'}
          </h2>
          <div className="notes-cases-cards-container">
            {notesList.map(item => (
              <div key={item.id} className="note-case-card">
                <div>
                  <div className="card-top-header">
                    <h4 className="card-item-name">{item.name}</h4>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      {(item.status === 0 || item.status === '0') && (
                        <span style={{ fontSize: '10px', fontWeight: '700', textTransform: 'uppercase', backgroundColor: '#fee2e2', color: '#ef4444', border: '1px solid #fca5a5', padding: '2px 8px', borderRadius: '10px' }}>
                          Closed
                        </span>
                      )}
                      <span className={`priority-badge ${item.priority || 'medium'}`}>
                        {item.priority || 'medium'}
                      </span>
                    </div>
                  </div>
                  <div className="card-item-tags">{item.tags || '—'}</div>
                  <p style={{ fontSize: '13px', color: '#475569', margin: '4px 0 12px 0', lineClamp: 2, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                    {item.description}
                  </p>
                </div>

                <div className="card-footer-row">
                  <button 
                    type="button" 
                    className="card-view-more-btn"
                    onClick={() => handleViewDetails(item)}
                  >
                    {t('notesCases.viewMore') || 'View More >'}
                  </button>

                  <div className="card-action-icons">
                    {canEditNotesCases && (
                      <button
                        type="button"
                        className="card-icon-btn"
                        disabled={item.status === 0 || item.status === '0'}
                        onClick={() => {
                          if (item.status === 0 || item.status === '0') {
                            toast.warning('Closed notes cannot be edited.');
                            return;
                          }
                          setItemToEdit(item);
                          setView('edit');
                        }}
                        style={(item.status === 0 || item.status === '0') ? { opacity: 0.35, cursor: 'not-allowed' } : {}}
                        title={(item.status === 0 || item.status === '0') ? "Closed notes cannot be edited" : "Edit Note"}
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                          <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                        </svg>
                      </button>
                    )}
                    {canDeleteNotesCases && (
                      <button
                        type="button"
                        className="card-icon-btn delete"
                        onClick={() => {
                          setItemToDelete(item);
                          setShowDeleteModal(true);
                        }}
                        title="Delete Note"
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <polyline points="3 6 5 6 21 6" />
                          <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                        </svg>
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
            {notesList.length === 0 && (
              <EmptyState title="No Notes Found" subtitle="No active notes match your search criteria." hasSearch={true} />
            )}
          </div>
        </div>
      )}

      {/* Global Pagination Component */}
      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        totalCount={totalCount}
        onPageChange={(page) => setCurrentPage(page)}
      />

      {/* Primary Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteModal}
        type="danger"
        title={`Delete ${itemToDelete?.type === 'case' ? 'Case' : 'Note'}`}
        message={`Are you sure you want to delete "${itemToDelete?.name || 'this record'}"?`}
        confirmLabel="Delete"
        cancelLabel="Cancel"
        onConfirm={handleConfirmDelete}
        onCancel={() => {
          setShowDeleteModal(false);
          setItemToDelete(null);
        }}
      />
      </div>
    </ProtectedPage>
  );
}

export default NotesCases;
